import { useState, useEffect, useCallback, useRef } from "react";

function formatWithCommas(v) {
  if (v === "" || v == null) return "";
  const str = String(v).replace(/,/g, "");
  if (str === "" || str === "-") return str;
  const parts = str.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
}

function stripCommas(v) {
  return String(v).replace(/,/g, "");
}

export default function NumericInput({ value, onChange, name, style, className, ...rest }) {
  const [display, setDisplay] = useState(() => formatWithCommas(value));
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!focused) {
      setDisplay(formatWithCommas(value));
    }
  }, [value, focused]);

  const handleChange = useCallback((e) => {
    const raw = e.target.value;
    const sanitized = raw.replace(/[^0-9.,-]/g, "");
    const numeric = stripCommas(sanitized);

    // Format live with commas
    const formatted = formatWithCommas(sanitized);

    // Calculate cursor position adjustment
    const el = inputRef.current;
    if (el) {
      const cursorPos = el.selectionStart;
      const commasBefore = (sanitized.slice(0, cursorPos).match(/,/g) || []).length;
      const commasAfter = (formatted.slice(0, cursorPos + (formatted.length - sanitized.length)).match(/,/g) || []).length;
      const newCursor = cursorPos + (commasAfter - commasBefore);
      requestAnimationFrame(() => {
        el.setSelectionRange(newCursor, newCursor);
      });
    }

    setDisplay(formatted);

    if (onChange) {
      onChange({
        target: { value: numeric, name: name || e.target.name },
        preventDefault: () => {},
        stopPropagation: () => {},
      });
    }
  }, [onChange, name]);

  const handleFocus = useCallback(() => setFocused(true), []);

  const handleBlur = useCallback(() => {
    setFocused(false);
    const numeric = stripCommas(display);
    if (numeric === "" || numeric === "-") {
      setDisplay(numeric);
      return;
    }
    const num = Number(numeric);
    if (!Number.isNaN(num)) {
      setDisplay(formatWithCommas(num));
    }
  }, [display]);

  return (
    <input
      {...rest}
      ref={inputRef}
      type="text"
      inputMode="decimal"
      name={name}
      className={className}
      style={style}
      value={display}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
}
